// Echter Kalender im Reiter „Flow & Kalender“ (Phase 2.D, Ausbau): Tag/Woche/Monat mit Zeitraster 7–21 Uhr,
// Jetzt-Linie, Ebenen Till/Lars/Maren (Flow) und Minimusikertage aus dem CRM (ganztägig, nur lesen).
// Anlegen per Klick in eine freie Stunde, Verschieben per Drag & Drop (schreibt in Flow).
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TEAM, isUserKey, type UserKey } from '@mail/shared';
import { api, type KalenderDaten } from '../lib/api.ts';
import { useStore } from '../lib/store.ts';
import { toast } from './Toast.tsx';

type View = 'tag' | 'woche' | 'monat';
const H0 = 7;
const H1 = 21;
const PX_H = 44; // Pixel je Stunde
const WT = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const addDays = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
const monday = (d: Date) => addDays(startOfDay(d), -((d.getDay() + 6) % 7));
const iso = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const same = (a: Date, b: Date) => iso(a) === iso(b);
function kw(d: Date) {
  const x = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const day = x.getUTCDay() || 7;
  x.setUTCDate(x.getUTCDate() + 4 - day);
  const start = new Date(Date.UTC(x.getUTCFullYear(), 0, 1));
  return Math.ceil(((x.getTime() - start.getTime()) / 864e5 + 1) / 7);
}

type Layers = Record<UserKey | 'crm' | 'aufgaben', boolean>;
const LAYER_DEFAULT: Layers = { till: true, lars: true, maren: true, crm: true, aufgaben: true };

type Item = {
  key: string;
  titel: string;
  start: Date;
  end: Date;
  allDay: boolean;
  farbe: string;
  art: 'event' | 'task' | 'crm';
  owner: UserKey | null;
  url?: string | undefined;
  flowId?: number | undefined;
  untertitel?: string | undefined;
};

// `refresh`: Zähler von außen – ändert er sich (neue Aufgabe/Termin aus den Nachbarspalten), wird neu geladen
export function Kalender({ refresh = 0 }: { refresh?: number } = {}) {
  const me = useStore((s) => s.me);
  const [view, setView] = useState<View>(() => {
    try {
      return (localStorage.getItem('mm.kal.view') as View) || 'woche';
    } catch {
      return 'woche';
    }
  });
  const [anchor, setAnchor] = useState(() => startOfDay(new Date()));
  const [layers, setLayers] = useState<Layers>(() => {
    try {
      return {
        ...LAYER_DEFAULT,
        ...(JSON.parse(localStorage.getItem('mm.kal.layers') ?? '{}') as Partial<Layers>),
      };
    } catch {
      return LAYER_DEFAULT;
    }
  });
  const [daten, setDaten] = useState<KalenderDaten | null>(null);
  const [tick, setTick] = useState(0);
  const gridRef = useRef<HTMLDivElement>(null);

  const bereich = useMemo(() => {
    if (view === 'tag') return { from: anchor, to: addDays(anchor, 1) };
    if (view === 'woche') return { from: monday(anchor), to: addDays(monday(anchor), 7) };
    const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const from = monday(first);
    return { from, to: addDays(from, 42) };
  }, [view, anchor]);

  const load = useCallback(() => {
    void api
      .flowKalender(bereich.from.toISOString(), bereich.to.toISOString())
      .then(setDaten)
      .catch((e: Error) => toast(e.message));
  }, [bereich.from, bereich.to]);
  useEffect(() => void load(), [load, refresh]);
  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 60_000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    try {
      localStorage.setItem('mm.kal.view', view);
      localStorage.setItem('mm.kal.layers', JSON.stringify(layers));
    } catch {
      /* egal */
    }
  }, [view, layers]);
  // Beim Öffnen zur aktuellen Stunde scrollen
  useEffect(() => {
    const el = gridRef.current;
    if (!el) return;
    const h = new Date().getHours();
    el.scrollTop = Math.max(0, (h - H0 - 1) * PX_H);
  }, [view]);

  // Flow-Nutzer → Team-Key über die E-Mail (till@… → till)
  const keyOf = useCallback(
    (userId: number | null): UserKey | null => {
      const u = daten?.users.find((x) => x.id === userId);
      const k = u?.email.split('@')[0]?.toLowerCase();
      return isUserKey(k) ? k : null;
    },
    [daten],
  );

  const items = useMemo<Item[]>(() => {
    if (!daten) return [];
    const out: Item[] = [];
    for (const e of daten.events) {
      const owner = keyOf(e.ownerId) ?? e.attendeeIds.map(keyOf).find(Boolean) ?? null;
      if (owner && !layers[owner]) continue;
      if (!owner && !layers.till) continue;
      out.push({
        key: `e${e.id}`,
        titel: e.title,
        start: new Date(e.startDatetime),
        end: e.allDay ? addDays(startOfDay(new Date(e.endDatetime)), 1) : new Date(e.endDatetime),
        allDay: e.allDay,
        farbe: owner ? TEAM[owner].color : '#64748b',
        art: 'event',
        owner,
        flowId: e.id,
        untertitel: e.type,
      });
    }
    if (layers.aufgaben)
      for (const t of daten.tasks) {
        if (!t.dueDate || t.status === 'done') continue;
        const owner = keyOf(t.assigneeId);
        if (owner && !layers[owner]) continue;
        const d = startOfDay(new Date(t.dueDate));
        out.push({
          key: `t${t.id}`,
          titel: `☑ ${t.id} ${t.title}`,
          start: d,
          end: addDays(d, 1),
          allDay: true,
          farbe: owner ? TEAM[owner].color : '#94a3b8',
          art: 'task',
          owner,
          url: t.externalUrl ?? undefined,
        });
      }
    if (layers.crm)
      for (const c of daten.crm) {
        const d = startOfDay(new Date(c.datum + 'T00:00:00'));
        out.push({
          key: `c${c.id}`,
          titel: `${c.typ === 'Schulsong-Aufnahme' ? '🎵' : '🎤'} ${c.einrichtung?.name ?? c.name}`,
          start: d,
          end: addDays(d, 1),
          allDay: true,
          farbe: 'var(--crm-ink)',
          art: 'crm',
          owner: null,
          url: c.crmUrl,
          untertitel: `${c.modell}${c.team.length ? ' · ' + c.team.join(', ') : ''}${c.region ? ' · ' + c.region : ''}`,
        });
      }
    return out;
  }, [daten, layers, keyOf]);

  const tage = useMemo(() => {
    const n = view === 'tag' ? 1 : view === 'woche' ? 7 : 42;
    return Array.from({ length: n }, (_, i) => addDays(bereich.from, i));
  }, [view, bereich.from]);

  const anlegen = async (tag: Date, stunde: number) => {
    const title = prompt(`Neuer Termin am ${tag.toLocaleDateString('de-DE')} um ${stunde}:00 Uhr`);
    if (!title?.trim()) return;
    const start = new Date(tag.getFullYear(), tag.getMonth(), tag.getDate(), stunde, 0, 0, 0);
    const end = new Date(start.getTime() + 3600e3);
    try {
      await api.flowTerminAnlegen({
        title: title.trim(),
        start: start.toISOString(),
        end: end.toISOString(),
      });
      load();
    } catch (e) {
      toast((e as Error).message);
    }
  };
  const verschieben = async (it: Item, neuStart: Date) => {
    if (!it.flowId) return;
    const dauer = it.end.getTime() - it.start.getTime();
    try {
      await api.flowTerminAendern(it.flowId, {
        start: neuStart.toISOString(),
        end: new Date(neuStart.getTime() + dauer).toISOString(),
      });
      load();
    } catch (e) {
      toast((e as Error).message);
    }
  };
  // Löschen geht nur in Flow selbst (der Service-Zugang darf nicht löschen, Review 20.10.)
  const loeschen = (it: Item) => {
    if (it.flowId) toast('Löschen geht in Flow selbst (Link „Flow öffnen“ oben).');
  };

  // Drag & Drop: Termin greifen, in 15-Minuten-Schritten verschieben, loslassen → PATCH
  const drag = useRef<{ it: Item; dy: number; dx: number; startY: number; startX: number } | null>(
    null,
  );
  const onMouseDown = (it: Item, e: React.MouseEvent) => {
    if (it.art !== 'event' || it.allDay || e.button !== 0) return;
    drag.current = { it, dy: 0, dx: 0, startY: e.clientY, startX: e.clientX };
    const move = (ev: MouseEvent) => {
      if (!drag.current) return;
      drag.current.dy = ev.clientY - drag.current.startY;
      drag.current.dx = ev.clientX - drag.current.startX;
      setTick((x) => x + 1);
    };
    const up = () => {
      const d = drag.current;
      drag.current = null;
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
      if (!d) return;
      const minuten = Math.round((d.dy / PX_H) * 4) * 15;
      const spalte = gridRef.current ? (gridRef.current.clientWidth - 48) / tage.length : 1;
      const tageDelta = view === 'tag' ? 0 : Math.round(d.dx / spalte);
      if (!minuten && !tageDelta) return;
      const neu = new Date(d.it.start.getTime() + minuten * 60e3 + tageDelta * 864e5);
      void verschieben(d.it, neu);
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    e.preventDefault();
  };

  const titel =
    view === 'monat'
      ? anchor.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })
      : view === 'tag'
        ? anchor.toLocaleDateString('de-DE', {
            weekday: 'long',
            day: '2-digit',
            month: 'long',
            year: 'numeric',
          })
        : `${bereich.from.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })} – ${addDays(bereich.to, -1).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })} · KW ${kw(bereich.from)}`;
  const schritt = (n: number) =>
    setAnchor((a) =>
      view === 'monat'
        ? new Date(a.getFullYear(), a.getMonth() + n, 1)
        : addDays(a, n * (view === 'tag' ? 1 : 7)),
    );
  const now = new Date();
  void tick;

  return (
    <div className="kal">
      <div className="kal-top">
        <button className="btn sm" onClick={() => setAnchor(startOfDay(new Date()))}>
          Heute
        </button>
        <button className="ib" aria-label="zurück" onClick={() => schritt(-1)}>
          ‹
        </button>
        <button className="ib" aria-label="vor" onClick={() => schritt(1)}>
          ›
        </button>
        <b className="kal-titel">{titel}</b>
        <span className="sp" />
        <div className="side-tabs kal-views" role="tablist">
          {(['tag', 'woche', 'monat'] as const).map((v) => (
            <button
              key={v}
              role="tab"
              aria-selected={view === v}
              className={view === v ? 'on' : ''}
              onClick={() => setView(v)}
            >
              {{ tag: 'Tag', woche: 'Woche', monat: 'Monat' }[v]}
            </button>
          ))}
        </div>
      </div>
      <div className="kal-layers">
        {(['till', 'lars', 'maren'] as const).map((k) => (
          <label key={k} style={{ color: TEAM[k].color }}>
            <input
              type="checkbox"
              checked={layers[k]}
              onChange={(e) => setLayers({ ...layers, [k]: e.target.checked })}
            />{' '}
            {TEAM[k].name}
            {me?.key === k ? ' (ich)' : ''}
          </label>
        ))}
        <label style={{ color: 'var(--crm-ink)' }}>
          <input
            type="checkbox"
            checked={layers.crm}
            onChange={(e) => setLayers({ ...layers, crm: e.target.checked })}
          />{' '}
          Minimusikertage & Aufnahmen (CRM)
        </label>
        <label>
          <input
            type="checkbox"
            checked={layers.aufgaben}
            onChange={(e) => setLayers({ ...layers, aufgaben: e.target.checked })}
          />{' '}
          Aufgaben
        </label>
        {daten?.error && <span className="pill warn">{daten.error}</span>}
        {daten && !daten.available && (
          <span className="pill warn">Flow nicht angebunden – nur CRM-Termine</span>
        )}
      </div>

      {view === 'monat' ? (
        <div className="kal-monat">
          {WT.map((w) => (
            <div key={w} className="kal-wt">
              {w}
            </div>
          ))}
          {tage.map((d) => {
            const list = items.filter((it) => it.start < addDays(d, 1) && it.end > d);
            return (
              <div
                key={iso(d)}
                className={`kal-zelle${d.getMonth() !== anchor.getMonth() ? ' aus' : ''}${same(d, now) ? ' heute' : ''}`}
                onDoubleClick={() => {
                  setAnchor(d);
                  setView('tag');
                }}
              >
                <div className="kal-zd">{d.getDate()}</div>
                {list.slice(0, 4).map((it) => (
                  <ItemChip key={it.key} it={it} onDelete={loeschen} />
                ))}
                {list.length > 4 && (
                  <div className="muted" style={{ fontSize: 11 }}>
                    +{list.length - 4}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <>
          <div
            className="kal-kopf"
            style={{ gridTemplateColumns: `48px repeat(${tage.length}, 1fr)` }}
          >
            <div />
            {tage.map((d) => (
              <div key={iso(d)} className={`kal-kd${same(d, now) ? ' heute' : ''}`}>
                <span>{WT[(d.getDay() + 6) % 7]}</span> <b>{d.getDate()}</b>
              </div>
            ))}
            <div className="kal-ganz-l">ganztägig</div>
            {tage.map((d) => (
              <div key={'g' + iso(d)} className="kal-ganz">
                {items
                  .filter((it) => it.allDay && it.start < addDays(d, 1) && it.end > d)
                  .map((it) => (
                    <ItemChip key={it.key} it={it} onDelete={loeschen} />
                  ))}
              </div>
            ))}
          </div>
          <div
            className="kal-grid"
            ref={gridRef}
            style={{ gridTemplateColumns: `48px repeat(${tage.length}, minmax(0, 1fr))` }}
          >
            <div className="kal-std">
              {Array.from({ length: H1 - H0 }, (_, i) => (
                <div key={i} style={{ height: PX_H }}>
                  {H0 + i}:00
                </div>
              ))}
            </div>
            {tage.map((d) => {
              const list = items.filter((it) => !it.allDay && same(it.start, d));
              return (
                <div
                  key={iso(d)}
                  className={`kal-spalte${same(d, now) ? ' heute' : ''}`}
                  style={{ height: (H1 - H0) * PX_H }}
                >
                  {Array.from({ length: H1 - H0 }, (_, i) => (
                    <div
                      key={i}
                      className="kal-slot"
                      style={{ top: i * PX_H, height: PX_H }}
                      onDoubleClick={() => void anlegen(d, H0 + i)}
                      title="Doppelklick: Termin anlegen"
                    />
                  ))}
                  {same(d, now) && now.getHours() >= H0 && now.getHours() < H1 && (
                    <div
                      className="kal-jetzt"
                      style={{ top: (now.getHours() - H0 + now.getMinutes() / 60) * PX_H }}
                    />
                  )}
                  {list.map((it) => {
                    const s = it.start.getHours() + it.start.getMinutes() / 60;
                    const e = Math.max(
                      s + 0.5,
                      it.end.getHours() + it.end.getMinutes() / 60 + (same(it.end, d) ? 0 : 24),
                    );
                    const dragging = drag.current?.it.key === it.key ? drag.current : null;
                    return (
                      <div
                        key={it.key}
                        className="kal-ev"
                        style={{
                          top: (Math.max(H0, s) - H0) * PX_H + (dragging?.dy ?? 0),
                          height: Math.max(18, (Math.min(H1, e) - Math.max(H0, s)) * PX_H - 2),
                          left: dragging ? dragging.dx : 2,
                          borderLeftColor: it.farbe,
                          background: `color-mix(in srgb, ${it.farbe} 14%, var(--bg-surface))`,
                        }}
                        onMouseDown={(ev) => onMouseDown(it, ev)}
                        onDoubleClick={() => loeschen(it)}
                        title={`${it.titel} · ${it.start.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}–${it.end.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}${it.owner ? ' · ' + TEAM[it.owner].name : ''} (ziehen = verschieben)`}
                      >
                        <div className="kal-ev-t">{it.titel}</div>
                        <div className="kal-ev-z">
                          {it.start.toLocaleTimeString('de-DE', {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                          {it.owner ? ` · ${TEAM[it.owner].name}` : ''}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

function ItemChip({ it, onDelete }: { it: Item; onDelete: (it: Item) => void }) {
  const inner = (
    <span
      className="kal-chip"
      style={{
        background: `color-mix(in srgb, ${it.farbe} 18%, var(--bg-surface))`,
        borderLeftColor: it.farbe,
      }}
      title={`${it.titel}${it.untertitel ? ' · ' + it.untertitel : ''}`}
      onDoubleClick={() => it.art === 'event' && onDelete(it)}
    >
      {it.titel}
    </span>
  );
  return it.url ? (
    <a
      href={it.url}
      target={it.art === 'crm' ? '_blank' : undefined}
      rel="noreferrer"
      style={{ textDecoration: 'none', color: 'inherit' }}
    >
      {inner}
    </a>
  ) : (
    inner
  );
}
